import React, { useCallback, useEffect, useMemo, useState } from "react";

import ContainedButton from "components/layout/form/containedButton";
import Loading from "components/layout/loading";
import Navbar from "components/MI/Navbar";
import OutlinedButton from "components/layout/form/outlinedButton";
import PersonalData from "./PersonalData";
import RegistrationData from "./RegistrationData";
import UserMenu from "components/layout/userMenuNew";
import { requestGetUser, requestUserInfo } from "./services";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import { useTour } from "hooks/useTour";

import TourBalloon from "components/NewTour/TourBallon";
import {
  Body,
  Container,
  ContractAdhesion,
  ContractAdhesionWrap,
  Header,
  HeaderInfos,
  HeaderSubtitle,
  HeaderTitle,
  PanelContainer,
} from "./styles";

type CustomerAddressesData = {
  addressLine: string;
  addressNumber: string;
  complement: string;
  city: string;
  stateCode: string;
  siteId: string;
  addressType: string;
};

export type UserInfoData = {
  // socialName: string;
  birthDate: string;
  phoneDdd: string;
  phoneNumber: string;
  email: string;
  document: string;
  userId: string;
  customerCode: string;
  corporateName: string;
  customerSiteId: string;
  customerAddresses: CustomerAddressesData[];
};

const Profile: React.FC = () => {
  const [loading, setLoading] = useState<boolean>();
  const [editMode, setEditMode] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfoData>();
  const [activeForm, setActiveForm] = useState<"personal" | "registration">(
    "personal"
  );

  const showMobileMode = useMediaQuery({ maxWidth: 769 });
  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  const getInfoData = useCallback(async () => {
    try {
      setLoading(true);
      const responseUserInfo = await requestUserInfo();
      const responseUserId = await requestGetUser();
      setUserInfo({ ...responseUserInfo, ...responseUserId });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      const erro = err.response;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  }, []);

  useEffect(() => {
    getInfoData();
  }, []);

  const birthDate = useMemo(() => {
    if (userInfo && userInfo.birthDate) {
      const dateSplit = userInfo.birthDate.split("-");
      return `${dateSplit[2]}/${dateSplit[1]}/${dateSplit[0]}`;
    }
    return null;
  }, [userInfo]);

  const phoneNumber = useMemo(() => {
    if (userInfo && userInfo.phoneDdd && userInfo.phoneNumber) {
      return `(${userInfo.phoneDdd}) ${userInfo.phoneNumber.slice(
        0,
        5
      )}-${userInfo.phoneNumber.slice(5)}`;
    }
    return null;
  }, [userInfo]);

  const shipToAddress = useMemo(() => {
    const addressToFilter = userInfo?.customerAddresses?.length
      ? userInfo?.customerAddresses
      : [];
    const [addressFilttered] = addressToFilter.filter(
      ({ siteId, addressType }) =>
        siteId === userInfo?.customerSiteId && addressType === "SHIP_TO"
    );
    return (
      `${addressFilttered?.addressLine} ${
        addressFilttered?.addressNumber
          ? `, ${addressFilttered?.addressNumber}`
          : ""
      } ${
        addressFilttered?.complement ? `, ${addressFilttered?.complement}` : ""
      } ${
        addressFilttered?.city
          ? `, ${addressFilttered?.city}-${addressFilttered?.stateCode}`
          : ""
      }` || ""
    );
  }, [userInfo]);

  const shipLetterToAddress = useMemo(() => {
    const addressToFilter = userInfo?.customerAddresses?.length
      ? userInfo?.customerAddresses
      : [];
    const [addressFilttered] = addressToFilter.filter(
      ({ siteId, addressType }) =>
        siteId === userInfo?.customerSiteId && addressType === "SHIP_LETTER_TO"
    );
    return (
      `${addressFilttered?.addressLine} ${
        addressFilttered?.addressNumber
          ? `, ${addressFilttered?.addressNumber}`
          : ""
      } ${
        addressFilttered?.complement ? `, ${addressFilttered?.complement}` : ""
      } ${
        addressFilttered?.city
          ? `, ${addressFilttered?.city}-${addressFilttered?.stateCode}`
          : ""
      }` || ""
    );
  }, [userInfo]);

  useEffect(() => {
    setTourType("meus-dados-novo");
    setSteps(1);
  }, [showTour, setTourType, setSteps]);

  return (
    <Navbar selected="meus-dados-novo">
      <UserMenu intro />
      <Container>
        <Header>
          <HeaderInfos>
            <HeaderTitle>
              {activeForm === "personal" ? "Meus dados" : "Dados cadastrais"}
            </HeaderTitle>
            <HeaderSubtitle>
              Verifique se todos os seus dados estão corretos.
            </HeaderSubtitle>
          </HeaderInfos>
          {!showMobileMode && (
            <ContractAdhesion
              href="/static/TermoDeAdesaoMI.pdf"
              target="_blank"
            >
              Termo de adesão
            </ContractAdhesion>
          )}
        </Header>
        {showMobileMode && (
          <ContractAdhesionWrap>
            <ContractAdhesion
              href="/static/TermoDeAdesaoMI.pdf"
              target="_blank"
            >
              Termo de adesão
            </ContractAdhesion>
          </ContractAdhesionWrap>
        )}
        <Body>
          <PanelContainer
            showTour={
              tourStep === 1 && tourType === "meus-dados-novo" && showTour
            }
          >
            {activeForm === "personal" && (
              <>
                <PersonalData
                  editMode={editMode}
                  setEditMode={setEditMode}
                  // socialName={userInfo?.socialName}
                  birthDate={birthDate}
                  email={userInfo?.email}
                  phoneNumber={phoneNumber}
                  document={userInfo?.document}
                  userId={userInfo?.userId}
                  customerCode={userInfo?.customerCode}
                />
                {!editMode && (
                  <ContainedButton
                    style={{
                      maxWidth: showMobileMode ? "100%" : "85%",
                    }}
                    type="button"
                    onClick={() => setEditMode(true)}
                  >
                    Editar dados pessoais
                  </ContainedButton>
                )}
                {!editMode && (
                  <OutlinedButton
                    style={{
                      maxWidth: showMobileMode ? "100%" : "85%",
                    }}
                    type="button"
                    onClick={() => setActiveForm("registration")}
                  >
                    Ver dados cadastrais
                  </OutlinedButton>
                )}
              </>
            )}
            {activeForm === "registration" && (
              <>
                <RegistrationData
                  corporateName={userInfo?.corporateName}
                  shipToAddress={shipToAddress}
                  shipLetterToAddress={shipLetterToAddress}
                  customerCode={userInfo?.customerCode}
                  document={userInfo?.document}
                />
                <ContainedButton
                  style={{
                    maxWidth: showMobileMode ? "100%" : "85%",
                  }}
                  type="button"
                  onClick={() => setActiveForm("personal")}
                >
                  Voltar para meus dados
                </ContainedButton>
              </>
            )}
            {showTour && tourStep === 1 && tourType === "meus-dados-novo" && (
              <TourBalloon
                title="Meus dados"
                content="Aqui você encontra o Termo de adesão e as informações do seu cadastro. É possível ainda realizar a troca de senha clicando em Alterar senha."
                position={{
                  left: "592px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  arrowPosition: "left",
                  arrowLeft: "-10%",
                  maxWidth: 320,
                }}
              />
            )}
          </PanelContainer>
        </Body>
      </Container>
      {loading && <Loading />}
    </Navbar>
  );
};

export default Profile;
